import { Component, Inject, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { catchError, finalize, map, mapTo, startWith, switchMap, take, tap } from 'rxjs/operators'
import { SendContractService } from '../send-contract.service'
import {
  createPlaceFieldsForm,
  PlaceFieldsFormComponent,
} from '../../template-drawer/components/place-fields-form/place-fields-form.component'
import { createTemplateBasicForm } from '../../template-drawer/components/upload-template/upload-template.component'
import { createSendContractInfoForm } from '../../template-drawer/components/send-contract-info-form/send-contract-info-form.component'
import {
  createSignersForm,
  SignerItemFormDataWithoutRole,
} from '../components/signers-without-role/signers-without-role.component'
import * as lodash from 'lodash'
import { combineLatest, Observable, of } from 'rxjs'
import { getContractSigners, SignerOptionItem } from '../components/signer-input-base.component'
import { PreviewTemplateDrawerService } from '../../template-drawer/preview-template/preview-template-drawer.service'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { ActionFailed, Destroyable, Toaster, untilDestroy, validateForm } from '@tokeet-frontend/tv3-platform'
import { CustomCode } from '@tv3/store/custom-codes/custom-code.model'
import {
  Contract,
  ContractSignerType,
  ContractStatus,
  ContractTypes,
  CreateContract,
  CreateContractComplete,
  emailSubjectByTemplateType,
  getFillBeforeSendingFields,
  SignatureFileService,
  SignFieldDef,
  TemplateTypes,
  TemplateUploadResponse,
} from '@tokeet-frontend/signature'
import { AuthService } from '@tv3/services/auth.service'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Guest } from '@tv3/store/guest/guest.model'
import { SendDocumentDrawerData } from './send-document-drawer.service'
import { GuestService } from '@tv3/store/guest/guest.service'
import { SignDrawerService } from '@tv3/containers/documents/sign/sign-drawer.service'
import { selectAllCustomCodes } from '@tv3/store/custom-codes/custom-code.selectors'
import { MatStepper } from '@angular/material/stepper'

enum SendDocumentSteps {
  SelectDocument,
  AddSigner,
  PlaceFields,
  DocumentDetails,
}

@Component({
  selector: 'app-send-document-drawer',
  templateUrl: './send-document-drawer.component.html',
  styleUrls: ['./send-document-drawer.component.scss'],
})
export class SendDocumentDrawerComponent extends Destroyable implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper

  steps = SendDocumentSteps

  get currentStep() {
    return this.stepper?.selectedIndex
  }

  @ViewChild('placeFieldsFormCom') placeFieldsFormCom: PlaceFieldsFormComponent

  contractBasicForm = createTemplateBasicForm(this.fb)
  signersForm = createSignersForm(this.fb)
  fieldsForm = createPlaceFieldsForm(this.fb)
  contractInfoForm = createSendContractInfoForm(this.fb)

  customCodes: CustomCode[]

  templateTypes = TemplateTypes

  get isDocumentType() {
    return this.getFieldValue(this.contractBasicForm, 'type') === TemplateTypes.Document
  }

  isSending = false

  mergedDocumentFileUrl: string
  isProcessing = false

  onlyMeSigner = false

  inquiry: Inquiry | null = null
  defaultSigner: Guest | null = null

  constructor(
    private dialog: MatDialogRef<SendDocumentDrawerComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private actions: Actions,
    private toaster: Toaster,
    private authService: AuthService,
    public sendTemplateService: SendContractService,
    private guestService: GuestService,
    private previewDrawer: PreviewTemplateDrawerService,
    private fileService: SignatureFileService,
    private signDrawer: SignDrawerService,
    @Inject(MAT_DIALOG_DATA) private data: SendDocumentDrawerData
  ) {
    super()
  }

  getFieldValue(form: FormGroup, key: string) {
    return form.get(key)?.value
  }

  ngOnInit(): void {
    this.store.pipe(select(selectAllCustomCodes), untilDestroy(this)).subscribe((items) => {
      this.customCodes = items
    })

    if (this.data?.inquiry) {
      this.inquiry = this.data.inquiry
      this.defaultSigner = this.inquiry.guest || null
      this.contractInfoForm.patchValue({ inquiry: this.inquiry })
    }

    this.contractBasicForm
      .get('type')
      .valueChanges.pipe(startWith(this.contractBasicForm.get('type').value), untilDestroy(this))
      .subscribe((type) => {
        this.contractInfoForm.patchValue({
          title: emailSubjectByTemplateType[type],
        })
      })

    this.signersForm.valueChanges
      .pipe(
        map((items: SignerItemFormDataWithoutRole[]) => {
          return items.length === 1 && items[0].id === this.authService.user.id
        }),
        untilDestroy(this)
      )
      .subscribe((onlyMeSigner) => {
        this.onlyMeSigner = onlyMeSigner
      })
  }

  close() {
    this.dialog.close()
  }

  nextStep() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }
    if (this.currentStep === SendDocumentSteps.SelectDocument) {
      this.isProcessing = true
      this.mergeTemplates().subscribe({
        next: (url) => {
          this.mergedDocumentFileUrl = url
          this.stepper.next()
          this.isProcessing = false
        },
        error: (error) => {
          this.isProcessing = false
          this.store.dispatch(ActionFailed({ error }))
        },
      })
    } else {
      this.stepper.next()
    }
  }

  hasPrev() {
    return this.currentStep > SendDocumentSteps.SelectDocument
  }

  prevStep() {
    this.stepper.previous()
  }

  getFillBeforeSendingFields(fieldsForm: FormGroup) {
    const { fields } = fieldsForm.getRawValue()
    return getFillBeforeSendingFields(fields)
  }

  mergeTemplates() {
    const { uploaded } = this.contractBasicForm.getRawValue()

    const uploadedResults: TemplateUploadResponse[] = uploaded
    const urls = lodash.map(uploadedResults, (t) => t.pdf_file_url)
    if (urls.length > 1) {
      return this.fileService.merge(urls)
    } else {
      return of(lodash.head(urls))
    }
  }

  onPreview() {
    const { type } = this.contractBasicForm.getRawValue()
    const { font, font_size } = this.fieldsForm.getRawValue()
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    this.previewDrawer.open({
      type,
      fields,
      pdfFileUrl: this.mergedDocumentFileUrl,
      defaultFontSize: font_size,
      defaultFont: font,
    })
  }

  validateStep() {
    switch (this.currentStep) {
      case SendDocumentSteps.SelectDocument:
        return validateForm(this.contractBasicForm)
      case SendDocumentSteps.AddSigner:
        return validateForm(this.signersForm)
      case SendDocumentSteps.PlaceFields:
        return this.placeFieldsFormCom.isTemplateReady()
      case SendDocumentSteps.DocumentDetails:
        return validateForm(this.contractInfoForm)
    }
  }

  showValidationMessage() {
    switch (this.currentStep) {
      case SendDocumentSteps.SelectDocument:
        this.toaster.warning($localize`Please upload your template file.`)
        return
      case SendDocumentSteps.AddSigner:
        this.toaster.warning($localize`Correct the fields shown in red before going to next.`)
        return
      case SendDocumentSteps.PlaceFields:
        return this.toaster.warning(
          $localize`To proceed to the next step, please add at least 1 signature field for each signer of the document.`
        )
      case SendDocumentSteps.DocumentDetails:
        return
    }
  }

  getRoles(signersForm: FormGroup) {
    const signers: SignerItemFormDataWithoutRole[] = signersForm.getRawValue()
    return getContractSigners(signers, true).map((s) => s.role)
  }

  isBookingRequired(service: SendContractService, type: ContractTypes, fields: SignFieldDef[]) {
    return service.isBookingRequired(type, fields)
  }

  getContractPayload(signers: SignerOptionItem[]) {
    const currentUser = this.authService.currentUser
    const { type, uploaded } = this.contractBasicForm.getRawValue()
    const fields = this.placeFieldsFormCom.getFieldsConfig()
    const { name, title, message } = this.contractInfoForm.getRawValue()

    const uploadedResults: TemplateUploadResponse[] = uploaded

    const contractPayload = {
      origin_files: lodash.map(uploadedResults, (t) => t.origin),
      pdf_file_url: this.mergedDocumentFileUrl,
      template_id: 'none',
      name,
      title,
      message,
      type,
      fields,
      account: `${currentUser.account}`,
      user_id: currentUser.id,
      signers: getContractSigners(signers, true),
    }

    return contractPayload
  }

  resolveSigners(): Observable<SignerOptionItem[]> {
    const signers: SignerItemFormDataWithoutRole[] = this.signersForm.getRawValue()

    return combineLatest(
      lodash.map(signers, (signer: SignerItemFormDataWithoutRole) => {
        if (!signer.save || signer.email === signer.data?.primaryEmail) {
          return of(signer)
        }
        return this.guestService.create({ name: signer.name, email: signer.email }).pipe(
          map((guest): SignerOptionItem => {
            return {
              name: signer.name,
              email: signer.email,
              id: guest.id,
              type: ContractSignerType.Guest,
              role: signer.role,
              order: signer.order,
              data: guest,
              id_photo_required: signer.id_photo_required,
              selfie_photo_required: signer.selfie_photo_required,
            }
          }),
          catchError(() => of(signer))
        )
      })
    )
  }

  send(silent = false) {
    this.isSending = true
    return this.resolveSigners().pipe(
      map((signers) => this.getContractPayload(signers)),
      switchMap((contractPayload: Contract) => {
        const { inquiry } = this.contractInfoForm.getRawValue()
        return this.sendTemplateService.tryToFillWithTokeetData(contractPayload, inquiry).pipe(mapTo(contractPayload))
      }),
      tap((contractPayload) => this.store.dispatch(CreateContract({ payload: contractPayload, silent }))),
      switchMap(() =>
        this.actions.pipe(ofType(CreateContractComplete, ActionFailed), take(1), ofType(CreateContractComplete))
      ),
      finalize(() => (this.isSending = false))
    )
  }

  onSignIt() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }

    this.send(true)
      .pipe(
        switchMap(({ item }): Observable<any> => {
          if (item.status === ContractStatus.Pending) {
            return this.signDrawer.open(item.contract_id, item.signers?.[0].role).afterClosed()
          } else {
            return of(true)
          }
        }),
        tap(() => this.toaster.success($localize`Document is signed successfully.`)),
        tap(() => this.dialog.close())
      )
      .subscribe()
  }

  onSend() {
    if (!this.validateStep()) {
      this.showValidationMessage()
      return
    }

    this.send()
      .pipe(tap(() => this.dialog.close()))
      .subscribe()
  }
}
